import { UserAgentProvider } from "@quentin-sommer/react-useragent";
import * as sentry from "@sentry/remix";
import axios from "axios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relative from "dayjs/plugin/relativeTime";
import httpStatus from "http-status";
import jalali from "jalaliday";
import React from "react";
import { useTranslation } from "react-i18next";
import { Provider } from "react-redux";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  isRouteErrorResponse,
  useLoaderData,
  useMatches,
  useRouteError,
  useRouteLoaderData,
} from "react-router";
import { MetaFunction } from "react-router";
import { ToastContainer } from "react-toastify";
import reactToastifyStyles from "react-toastify/dist/ReactToastify.css?url";
import { useChangeLanguage } from "remix-i18next/react";
import { GlobalLoading } from "~/components/GlobalLoading";
import { ModalContainer } from "~/containers/Modal";
import * as cookies from "~/cookies";
import type { Design } from "~/cookies";
import tokenStyles from "~/design/tokens.css?url";
import { DesignContext } from "~/design/useDesign";
import i18next from "~/i18n.server";
import sharedStyles from "~/styles/shared.css?url";
import MessageCard from "./components/MessageCard";
import { patchResource } from "./services/api";
import createStore from "./services/store";
import tailwindStyles from "./tailwind.css?url";
import to from "./utils/to";

const { store } = createStore();

dayjs.extend(relative);
dayjs.extend(jalali);
dayjs.extend(duration);

export function links() {
  return [
    { rel: "preload", href: "/fonts/IRANYekanXVF.woff", as: "font", type: "font/woff", crossOrigin: "anonymous" },
    { rel: "preload", href: "/fonts/Peyda/woff2/PeydaWebFaNum-Black.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
    { rel: "stylesheet", href: tailwindStyles },
    { rel: "stylesheet", href: sharedStyles },
    { rel: "stylesheet", href: tokenStyles },
    { rel: "stylesheet", href: reactToastifyStyles },
    {
      rel: "apple-touch-icon",
      href: "/apple-touch-icon.png?hash=a93",
    },
    {
      sizes: "32x32",
      href: "/favicon-32x32.png",
      rel: "icon",
      type: "image/png",
    },
    {
      sizes: "16x16",
      href: "/favicon-16x16.png",
      rel: "icon",
      type: "image/png",
    },
    { href: "/site.webmanifest?hash=a9133", rel: "manifest" },
    { href: "/safari-pinned-tab.svg", rel: "mask-icon", color: "#5bbad5" },
  ];
}

export const meta: MetaFunction = () => {
  const { t } = useTranslation("translation");
  return [
    { title: t("title"), description: t("description") },
    { property: "og:title", content: t("og.title") },
    { property: "og:description", content: t("og.description") },
    { property: "og:url", content: "https://fonta.org" },
    { property: "og:image", content: "https://buy.fonta.org/img/appstore-banner.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "600" },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: t("og.site_name") },
    { property: "twitter:card", content: "summary_large_image" },
  ];
};

const THEME_COLORS: Record<Design, string> = { a: "#060a1c", b: "#f5f7fb", c: "#1a3e95" };
function ThemeColor({ design }: { design: Design }) {
  if (design === "b")
    return (
      <>
        <meta name="theme-color" content={THEME_COLORS.b} media="(prefers-color-scheme: light)" />
        <meta name="theme-color" content="#0a0e1b" media="(prefers-color-scheme: dark)" />
      </>
    );
  return <meta name="theme-color" content={THEME_COLORS[design]} />;
}

type ErrorBoundaryComponent = (props: { children: React.ReactNode }) => JSX.Element;
const ErrorBoundaryDocument: ErrorBoundaryComponent = (props: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const root = useRouteLoaderData("root") as LoaderData | undefined;
  const design = root?.design ?? "a";
  return (
    <html lang={i18n.language} dir={i18n.dir()} data-design={design} data-theme={design === "b" ? "light" : "dark"}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1,viewport-fit=cover" />
        <ThemeColor design={design} />
        <Meta />
        <Links />
      </head>
      <body>
        {props.children}
        {/* services/api.ts reads window.process on the client; without it error pages never hydrate */}
        <script dangerouslySetInnerHTML={{ __html: `window.process = ${JSON.stringify({ env: root?.ENV ?? {} })};` }} />
        <Scripts />
      </body>
    </html>
  );
};

export const UnWrappedErrorBoundary: ErrorBoundaryComponent = () => {
  const { t } = useTranslation();
  const error: unknown = useRouteError();
  console.log(error);
  sentry.captureRemixErrorBoundaryError(error);

  let status: number | undefined = httpStatus["INTERNAL_SERVER_ERROR"];
  if (isRouteErrorResponse(error)) status = error.status;
  else if (axios.isAxiosError(error)) status = error.response?.status;

  if (status === httpStatus["UNAUTHORIZED"]) {
    return (
      <ErrorBoundaryDocument>
        <MessageCard type="error" title={t("errors.401")} description={t("errors.401_message")} toDo={t("reload")} toDoHref="/" />
      </ErrorBoundaryDocument>
    );
  }
  if (status === httpStatus["INTERNAL_SERVER_ERROR"]) {
    return (
      <ErrorBoundaryDocument>
        <MessageCard type="error" title={t("errors.500")} description={t("errors.500_message")} toDo={t("backToHome")} toDoHref="/" />
      </ErrorBoundaryDocument>
    );
  }
  if (status === httpStatus["NOT_FOUND"]) {
    return (
      <ErrorBoundaryDocument>
        <MessageCard type="error" title={t("errors.404")} description={t("errors.404_message")} toDo={t("backToHome")} toDoHref="/" />
      </ErrorBoundaryDocument>
    );
  }
  if (status === httpStatus["CONFLICT"]) {
    return (
      <ErrorBoundaryDocument>
        <MessageCard title={t("errors.409")} description={t("errors.409_message")} toDo={t("reload")} toDoHref="/" />
      </ErrorBoundaryDocument>
    );
  }
  if (status === httpStatus["TOO_MANY_REQUESTS"]) {
    return (
      <ErrorBoundaryDocument>
        <MessageCard type="error" title={t("errors.429")} description={t("errors.429_message")} toDo={t("reload")} toDoHref="/" />
      </ErrorBoundaryDocument>
    );
  }

  return (
    <ErrorBoundaryDocument>
      <MessageCard
        type="error"
        title={t("errors.500")}
        description={t("errors.500_message")}
        toDo={t("reload")}
        toDoHref="/"
        // autoReloadAfter={5000}
      />
      <div className="container px-10">
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    </ErrorBoundaryDocument>
  );
};

export const ErrorBoundary = sentry.withSentry(UnWrappedErrorBoundary, {
  wrapWithErrorBoundary: false,
});

type LoaderData = {
  locale: string;
  cookie: unknown;
  design: Design;
  ua: string;
  ENV: any;
};
export let loader = async ({ request }) => {
  const locale = await i18next.getLocale(request);
  const design = await cookies.design.pick(request);
  let cookie = await cookies.auth.find(request);
  if (cookie) cookie = await cookies.auth.refresh(request);

  // get token from ?token=
  const url = new URL(request.url);
  const token = url.searchParams.get("token");
  if (token) return await cookies.auth.loginWithToken(request, token);

  return Response.json(
    {
      locale,
      cookie,
      design,
      ua: request.headers.get("user-agent"),
      ENV: { APP_API_URL: process.env.APP_API_URL, APP_URL: process.env.APP_URL, ASSETS_URL: process.env.ASSETS_URL },
    },
    {
      headers: [
        ["Set-Cookie", `${cookie ? await cookies.auth.cookie.serialize(cookie) : ""}; path=/;`],
        ["Set-Cookie", await cookies.lng.cookie.serialize(locale)],
        ["Set-Cookie", await cookies.design.cookie.serialize(design)],
      ],
    },
  );
};

// The checkout keeps its page in the query (?plan=, &paid=), so moving between its plans and payment page skips the round trip.
export const shouldRevalidate: ShouldRevalidateFunction = ({ currentUrl, nextUrl, formMethod }) => {
  return !!formMethod || currentUrl.pathname !== "/subscribe" || nextUrl.pathname !== "/subscribe";
};

export let handle = {
  // In the handle export, we can add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  // TIP: In most cases, you should set this to your defaultNS from your i18n config
  // or if you did not set one, set it to the i18next default namespace "translation"
  i18n: "translation",
};

function App() {
  // Get the locale from the loader
  let { locale, ENV, ua, cookie, design: loadedDesign } = useLoaderData<LoaderData>();
  // pinned for the life of the document; the loader re-runs on every navigation and could roll a new one
  const [design] = React.useState(loadedDesign);
  let { i18n } = useTranslation();
  const matches = useMatches();
  const match = matches.find((match) => match.data && match.data.links);
  const links = match?.data.links;

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files
  useChangeLanguage(locale);

  const changeUserLanguage = async (lng: string) => {
    if (cookie?.token && cookie?.user?.settings?.language !== locale) {
      const data = { settings: { language: locale } };
      const [err] = await to(
        patchResource({ token: cookie.token, resource: "users", resourceId: cookie.user._id, data: { settings: { language: data.lng } } }),
      );
      if (err) console.log(err);
    }
  };
  React.useEffect(() => {
    changeUserLanguage(locale);
  }, [locale, cookie?.user?._id]);

  React.useEffect(() => {
    if (cookie?.token) localStorage.setItem("auth", cookie.token);
  }, [cookie]);

  return (
    <html lang={i18n.language} dir={i18n.dir()} data-design={design} data-theme={design === "b" ? "light" : "dark"}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1,viewport-fit=cover" />
        <ThemeColor design={design} />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Fonta" />
        <meta name="apple-touch-fullscreen" content="yes" />
        <meta name="referrer" content="no-referrer" />

        <Meta />
        <Links />
        {links && links.map((link) => <link {...link} />)}
      </head>
      <body>
        <DesignContext.Provider value={design}>
          <GlobalLoading />
          <Provider store={store}>
            <UserAgentProvider ua={ua}>
              <>
                {/* each page renders its own <main> */}
                <div className="flex min-h-[100dvh] w-full flex-col">
                  <Outlet />
                  <ModalContainer />
                </div>
                <ToastContainer
                  position="bottom-center"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={i18n.dir() === "rtl"}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme={design === "b" ? "light" : "dark"}
                  stacked
                  toastClassName="!rounded-xl p-3 w-10 mx-auto"
                />
              </>
            </UserAgentProvider>
          </Provider>
        </DesignContext.Provider>
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.process = ${JSON.stringify({ env: ENV })};`,
          }}
        />

        <Scripts />
      </body>
    </html>
  );
}

export default sentry.withSentry(App);
